import { api } from "./base";
import { isTelegramMiniApp } from "@/shared/lib/telegram";
import { isMaxMiniApp } from "@/shared/lib/max";
/** Есть ли у пакета непустые стартовые начисления (см. `initial_grants`). */
function hasInitialGrants(pkg) {
    return Boolean(pkg.initial_grants && Object.keys(pkg.initial_grants).length);
}
/**
 * Цена пакета в рублях числом. Бэк отдаёт `price` строкой («990.00»), см.
 * `PackageResponse.price`. Всё, что считает и печатает цену, ходит сюда.
 */
export function getPackagePriceRub(pkg) {
    const value = Number(pkg.price);
    return Number.isFinite(value) ? value : 0;
}
/**
 * Сумма стартовых начислений пакета. Новый контракт — сумма значений
 * `initial_grants`; fallback на legacy `count`/`amount`, если бэк ещё на
 * старой схеме (или прислал пустой `{}`).
 */
export function getPackageGrantsTotal(pkg) {
    if (hasInitialGrants(pkg)) {
        return Object.values(pkg.initial_grants).reduce((a, b) => a + (b || 0), 0);
    }
    return pkg.count ?? pkg.amount ?? 0;
}
/**
 * Ключи баланса, которые дают песни. `subscription_songs` — начисления
 * подписочных пакетов (`BalanceType.SUBSCRIPTION_SONGS`): они тоже тратятся на
 * генерацию, поэтому пакет с ними — песенный. Без этого ключа возврат после
 * оплаты подписки не довёл бы пользователя до генерации.
 */
export const SONGS_GRANT_KEYS = ["songs", "subscription_songs"];
/**
 * Пакет начисляет песни? Новый контракт — есть песенный ключ в
 * `initial_grants`; fallback на legacy `type === "songs"`.
 */
export function isSongsPackage(pkg) {
    if (hasInitialGrants(pkg)) {
        return SONGS_GRANT_KEYS.some((key) => key in pkg.initial_grants);
    }
    return pkg.type === "songs";
}
/**
 * Пакет начисляет релизы? Пакеты RELEASE заводятся владельцем в дашборде с
 * `initial_grants = { releases: N }`; `kind === "release"` проверяем как
 * дополнительный признак.
 *
 * Основную фильтрацию делает бэк по `filters.grants = "releases"` (то же ИЛИ по
 * грантам и `kind`), здесь она остаётся страховкой на случай старого бэкенда,
 * который это поле ещё не знает и молча его игнорирует.
 */
export function isReleasePackage(pkg) {
    if (hasInitialGrants(pkg) && "releases" in pkg.initial_grants) {
        return true;
    }
    return pkg.kind === "release";
}
/**
 * Платформа текущего клиента для фильтрации пакетов:
 *  • Telegram WebApp → `tg`
 *  • MAX Mini App → `max`
 *  • моб. браузер / десктоп → `web`
 */
export function getCurrentPackagePlatform() {
    if (isTelegramMiniApp())
        return "tg";
    if (isMaxMiniApp())
        return "max";
    return "web";
}
/**
 * Пакет предназначен для текущей платформы? Например, `max`-пакеты видны
 * только внутри MAX, а в web/tg — нет. Если бэк не прислал `platform`
 * (legacy) — не скрываем.
 */
export function isPackageForCurrentPlatform(pkg) {
    if (!pkg.platform)
        return true;
    return pkg.platform === getCurrentPackagePlatform();
}
export const searchPackages = async (request = {}) => {
    const response = await api.post("/api/v1/public/packages/search", {
        limit: request.limit ?? 10,
        offset: request.offset ?? 0,
        // Пустой объект, а не null: у бэка `filters` не Optional, на null он
        // отвечает 422.
        filters: request.filters ?? {},
    });
    // Используем структуру, которая приходит с бэка (results)
    return response.data;
};
/**
 * Песенные пакеты для текущей платформы — ровно то, что показывает страница
 * пополнения `/balance`. Один источник для `/balance` и блока цен на
 * SEO-лендинге, чтобы фильтры не разъезжались.
 */
export const fetchSongsPackagesForCurrentPlatform = async () => {
    const response = await searchPackages({
        filters: { is_active: true, type: "songs" },
        limit: 100,
    });
    // Фильтруем на клиенте: пакеты с песнями (новый контракт + fallback) и
    // только под текущую платформу (tg/web). MAX-пакеты не показываем.
    return (response?.results || []).filter((pkg) => isSongsPackage(pkg) && isPackageForCurrentPlatform(pkg));
};
export const getPackage = async (packageId) => {
    const response = await api.get(`/api/v1/package/${packageId}`);
    return response.data;
};
